import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { ArrowRight, ChevronRight } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-base font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-700",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-muted text-gray-700 hover:text-gray-700",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        white: "bg-white text-gray-700 hover:bg-gray-100",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline !p-0",
        "link-secondary": "underline-offset-4 hover:underline !p-0 hover:text-primary",
        "link-white": "!p-0 text-white/80 hover:text-white",
      },
      size: {
        xs: "px-4 !py-3 text-sm",
        default: "px-4 !py-2 text-[15px]",
        sm: "px-6 py-3 text-sm font-normal",
        lg: "px-6 py-2.5",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(({ className, variant, size = "default", asChild = false, arrow = false, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  if (arrow) {
    const isLinkVariant = variant === "link" || variant === "link-secondary" || variant === "link-white";

    if (isLinkVariant) {
      return (
        <Comp className={cn(buttonVariants({ variant, size }), "group gap-1", className)} ref={ref} {...props}>
          <Slottable>{children}</Slottable>
          <ChevronRight className="transition-transform duration-300 group-hover:translate-x-1" />
        </Comp>
      );
    }

    const isLightVariant = variant === "outline" || variant === "white";
    const isXs = size === "xs";

    return (
      <Comp className={cn(buttonVariants({ variant, size }), "group md:pr-1.5", isXs ? "gap-2" : "gap-4", className)} ref={ref} {...props}>
        <Slottable>{children}</Slottable>
        <span
          className={cn(
            "max-md:hidden",
            "inline-flex shrink-0 items-center justify-center rounded-sm transition-transform duration-300 group-hover:translate-x-2",
            isXs ? "mr-1 p-1" : "mr-2 p-2",
            isLightVariant ? "bg-primary text-white" : "bg-white text-primary"
          )}
        >
          <ArrowRight
            className={cn({
              "size-5!": size === "lg",
              "size-4!": size === "default" || size === "sm",
              "size-3.5!": isXs,
            })}
          />
        </span>
      </Comp>
    );
  }

  return (
    <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
      {children}
    </Comp>
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
